import { css } from "@emotion/react";
import React, { useCallback, useRef, useState } from "react";
import { Popper, PopperBaseProps } from "../Popper";
import { hasOwnProp } from "../@utils/hasOwnProp";
import { useTheme } from "../theme";

const classNames = {
  tooltip: {
    arrow: "m2mui_Tooltip_arrow",
  },
};

export interface TooltipProps extends Pick<PopperBaseProps, "placement"> {
  /**
   * Tooltipに表示するテキスト
   */
  title: string;
  /**
   * Tooltipを表示する要素
   * 注意: disabledなボタン要素ではマウスイベントが発火しないため、Tooltipは機能しません
   */
  children: React.ReactElement | string;
  /**
   * 矢印を表示します。
   *
   * @default false
   */
  arrow?: boolean;
  /**
   * デフォルトの子要素にホバーした際に表示する機能を無視して、表示するかどうかを指定します。
   *
   * @default undefined
   */
  open?: boolean;
}

export const Tooltip = ({
  children,
  title,
  placement,
  arrow,
  open: openProp,
}: TooltipProps) => {
  const [node, setNode] = useState<HTMLElement | null>(null);

  const theme = useTheme();

  const isControlled = openProp !== undefined;

  const [mounted, setMounted] = useState(false);
  const prevOpen = useRef<boolean>(undefined);
  const [hidden, setHidden] = useState(true);

  if (isControlled && openProp !== prevOpen.current) {
    prevOpen.current = openProp;
    if (openProp) {
      setMounted(true);
      setHidden(false);
    } else {
      setHidden(true);
    }
  }

  const handleMouseEnter = useCallback(
    (event: React.MouseEvent, original?: React.MouseEventHandler) => {
      if (!isControlled) {
        setMounted(true);
        setHidden(false);
      }
      original?.(event);
    },
    [isControlled],
  );

  const handleMouseLeave = useCallback(
    (event: React.MouseEvent, original?: React.MouseEventHandler) => {
      if (!isControlled) {
        setHidden(true);
      }
      original?.(event);
    },
    [isControlled],
  );

  return (
    <React.Fragment>
      {typeof children === "string" ? (
        <span
          ref={setNode}
          onMouseEnter={(e) => handleMouseEnter(e)}
          onMouseLeave={(e) => handleMouseLeave(e)}
        >
          {children}
        </span>
      ) : (
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        React.cloneElement(children, {
          ref: setNode,
          onMouseEnter: (e: React.MouseEvent) =>
            handleMouseEnter(
              e,
              hasOwnProp(children.props, "onMouseEnter")
                ? (children.props.onMouseEnter as React.MouseEventHandler)
                : undefined,
            ),
          onMouseLeave: (e: React.MouseEvent) =>
            handleMouseLeave(
              e,
              hasOwnProp(children.props, "onMouseLeave")
                ? (children.props.onMouseLeave as React.MouseEventHandler)
                : undefined,
            ),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any)
      )}
      <Popper
        anchorEl={node}
        placement={placement}
        open={mounted}
        aria-hidden={hidden}
        disablePortal
        onTransitionEnd={() => {
          if (hidden) {
            setMounted(false);
          }
        }}
        css={css`
          opacity: 1;
          transition: opacity 200ms;
          &[aria-hidden="true"] {
            opacity: 0;
          }
          &[data-popper-placement*="bottom"] {
            .${classNames.tooltip.arrow} {
              top: 0;
              left: 12px;
              width: 12px;
              height: 6px;
              margin-top: -6px;
              &::before {
                border-color: transparent transparent currentcolor transparent;
                border-width: 0 6px 6px 6px;
              }
            }
            & > * {
              margin-top: 8px;
            }
          }
          &[data-popper-placement*="top"] {
            .${classNames.tooltip.arrow} {
              bottom: 0;
              left: 12px;
              width: 12px;
              height: 6px;
              margin-bottom: -6px;
              &::before {
                border-color: currentcolor transparent transparent transparent;
                border-width: 6px 6px 0 6px;
              }
            }
            & > * {
              margin-bottom: 8px;
            }
          }
          &[data-popper-placement*="right"] {
            .${classNames.tooltip.arrow} {
              top: 12px;
              left: 0;
              width: 6px;
              height: 12px;
              margin-left: -6px;
              &::before {
                border-color: transparent currentcolor transparent transparent;
                border-width: 6px 6px 6px 0;
              }
            }
            & > * {
              margin-left: 8px;
            }
          }
          &[data-popper-placement*="left"] {
            .${classNames.tooltip.arrow} {
              top: 12px;
              right: 0;
              width: 6px;
              height: 12px;
              margin-right: -6px;
              &::before {
                border-color: transparent transparent transparent currentcolor;
                border-width: 6px 0 6px 6px;
              }
            }
            & > * {
              margin-right: 8px;
            }
          }
        `}
      >
        <div
          css={[
            theme.typography.caption,
            css`
              position: relative;
              max-width: 300px;
              padding: 6px 10px;
              color: #fff;
              text-align: left;
              word-wrap: break-word;
              background-color: rgba(97, 97, 97, 0.9);
              border-radius: 4px;
            `,
          ]}
        >
          {title}
          {arrow ? (
            <div
              className={classNames.tooltip.arrow}
              css={css`
                position: absolute;
                color: rgba(97, 97, 97, 0.9);
                &::before {
                  display: block;
                  width: 0px;
                  height: 0px;
                  margin: auto;
                  content: "";
                  border-style: solid;
                }
              `}
            />
          ) : null}
        </div>
      </Popper>
    </React.Fragment>
  );
};
